import React, { useCallback, useEffect, useRef } from 'react';
import { makeStyles } from 'design-system/styles';
import ImageAdjustmentButton from 'react-scale/components/scalers/tasks/textcollection/ImageAdjustmentButton';
import _ from 'lodash';
import useShortcut from 'react-scale/hooks/useShortcut';
import Combokeys from 'combokeys';
// eslint-disable-next-line restrict-imports/restrict-imports
import { IconButton } from '@material-ui/core';

interface HorizontalBrightnessBlurSlidersProps {
  addBrightnessSlider?: boolean;
  addBlurSlider?: boolean;
  addZoomSlider?: boolean;
  setBrightness: React.Dispatch<React.SetStateAction<number>>;
  setBlur: React.Dispatch<React.SetStateAction<number>>;
  brightness: number;
  blur: number;
  zoom?: number;
  onZoomIncrease?: () => void;
  onZoomDecrease?: () => void;
  Minus?: () => JSX.Element;
  Plus?: () => JSX.Element;
  zoomLevels?: number[];
  formatPercent?: (value: number) => string;
}

const DEFAULT_BLUR = 0.25;

const HorizontalBrightnessBlurSliders: React.FC<HorizontalBrightnessBlurSlidersProps> = ({
  addBrightnessSlider,
  addBlurSlider,
  addZoomSlider,
  setBrightness,
  setBlur,
  brightness,
  blur,
  zoom,
  onZoomIncrease,
  onZoomDecrease,
  Minus,
  Plus,
  zoomLevels,
  formatPercent,
}) => {
  const brightnessRef = useRef<number>(0);
  const blurRef = useRef<number>(0);
  const zoomRef = useRef<number>(0);
  const classes = useStyles();

  const onBrightnessReset = useCallback(() => {
    setBrightness(0);
  }, [setBrightness]);

  const onBlurReset = useCallback(() => {
    setBlur(0);
  }, [setBlur]);

  useEffect(() => {
    brightnessRef.current = brightness;
    blurRef.current = blur;
    if (zoom) {
      zoomRef.current = zoom;
      const combokeys = new Combokeys(document.documentElement);

      combokeys.bind('plus', function () {
        onZoomIncrease!();
      });
      combokeys.bind('minus', function () {
        onZoomDecrease!();
      });
      return () => {
        combokeys.detach();
      };
    }
  }, [brightness, blur, zoom, onZoomDecrease, onZoomIncrease]);

  const increaseVisibility = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      brightnessRef.current = _.min([1, brightnessRef.current + 0.1])!;
      blurRef.current = _.min([1, blurRef.current + 0.1])!;
      setBrightness(brightnessRef.current);
      setBlur(blurRef.current);
    },
    [setBrightness, setBlur],
  );
  const decreaseVisibilty = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      brightnessRef.current = _.max([0, brightnessRef.current - 0.1])!;
      blurRef.current = _.max([0, blurRef.current - 0.1])!;
      setBrightness(brightnessRef.current);
      setBlur(blurRef.current);
    },
    [setBrightness, setBlur],
  );
  const unBlurAndBrighten = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      brightnessRef.current = 1;
      blurRef.current = 1;
      setBrightness(brightnessRef.current);
      setBlur(blurRef.current);
    },
    [setBrightness, setBlur],
  );
  const blurAndDarken = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      brightnessRef.current = DEFAULT_BLUR;
      blurRef.current = DEFAULT_BLUR;
      setBrightness(brightnessRef.current);
      setBlur(blurRef.current);
    },
    [setBrightness, setBlur],
  );

  useShortcut({ action: increaseVisibility, keydown: true, trigger: '.' });
  useShortcut({ action: decreaseVisibilty, keydown: true, trigger: ',' });
  useShortcut({ action: unBlurAndBrighten, keydown: true, trigger: ']' });
  useShortcut({ action: blurAndDarken, keydown: true, trigger: '[' });

  return (
    <>
      <div className={classes.footer}>
        {addZoomSlider &&
          zoom &&
          zoomLevels &&
          Minus &&
          Plus &&
          formatPercent &&
          onZoomIncrease &&
          onZoomDecrease && (
            <div className={classes.toolbar}>
              <IconButton disabled={zoom <= zoomLevels[0]} onClick={onZoomDecrease}>
                <Minus />
              </IconButton>
              <div className={classes.zoom}>{formatPercent(zoom)}</div>
              <IconButton
                disabled={zoom >= zoomLevels[zoomLevels.length - 1]}
                onClick={onZoomIncrease}
              >
                <Plus />
              </IconButton>
            </div>
          )}
        {addBrightnessSlider && (
          <div className={classes.toolbar}>
            <ImageAdjustmentButton
              value={brightness}
              onAdjust={setBrightness}
              iconName={'sun'}
              toolName={'Image Brightness'}
              onReset={onBrightnessReset}
              onlyShowSlider
            ></ImageAdjustmentButton>
          </div>
        )}
        {addBlurSlider && (
          <div className={classes.toolbar}>
            <ImageAdjustmentButton
              value={blur}
              onAdjust={setBlur}
              iconName={'eye'}
              toolName={'Image Blur'}
              onReset={onBlurReset}
              onlyShowSlider
            ></ImageAdjustmentButton>
          </div>
        )}
      </div>
    </>
  );
};

const useStyles = makeStyles({
  toolbar: {
    background: '#FFFFFF',
    boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.15)',
    borderRadius: '4px',
    textAlign: 'center',
    padding: '0px',
    margin: '4px',
  },
  footer: {
    position: 'absolute',
    bottom: '12px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginLeft: '-104px',
    width: '100%',
    opacity: 1,
    transition: 'opacity 0.25s',
    zIndex: 1000,
  },
  zoom: {
    display: 'inline-block',
    color: '#808080',
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
    textAlign: 'center',
    width: '4em',
    userSelect: 'none',
  },
});

export default HorizontalBrightnessBlurSliders;
